import { generateGuid } from "@1js/guid";
import type { ProductType, Prompt } from "@1js/pl-card-components";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { type HandoffCallbackResult } from "../hooks/handoff.types";
import {
  handoffAsync,
  type HandoffResponse,
  type IHandoffErrorResponseMessage,
} from "../services/augloop-client.web";
import type { HandoffFile } from "./workingsetfiles-client.web";

/**
 * The type of prompt
 */
export enum PromptType {
  Placeholder = "Placeholder",
  CIQ = "CIQ",
  PlainText = "Plain Text",
}

export enum HandoffContextFeatureName {
  AgentPromptTryInWXP = "AgentPromptTryInWXP",
}

/**
 * Categorizes the prompt type
 * @param displayText - The display text of the prompt
 * @returns The type of prompt
 */
const getPromptType = (displayText: string): PromptType => {
  if (displayText.includes("<placeholder>")) {
    return PromptType.Placeholder;
  } else if (displayText.includes("<entity")) {
    return PromptType.CIQ;
  } else {
    return PromptType.PlainText;
  }
};

/**
 * Type of Agent Context
 */
type AgentContext = {
  featureName: string;
  agentId: string;
};

/**
 * Get AgentContext from product and product
 * @param prompt prompt object to retrieve ProviderId
 * @param product product name of target app
 * @returns
 */
const getAgentContext = (
  prompt: Prompt,
  isAgentPromptHandoffToAugloopEnabled?: boolean
): AgentContext | null => {
  if (
    !!isAgentPromptHandoffToAugloopEnabled &&
    "ProviderId" in prompt &&
    prompt.ProviderId != "CopilotLab"
  ) {
    return {
      featureName: HandoffContextFeatureName.AgentPromptTryInWXP,
      agentId: prompt.ProviderId ?? "",
    };
  }
  return null;
};

/**
 * Checks if the response is an error
 * @param response - The response from the handoff service
 * @returns True if the response is an error
 */
const isError = (
  response: HandoffResponse
): response is IHandoffErrorResponseMessage => {
  return (<IHandoffErrorResponseMessage>response).error !== undefined;
};

/**
 * Handles handoff to Augloop Handoff service, to try prompts in Word, Excel, PowerPoint, Outlook, or OneNote.
 * @param _host - The host of SupportService
 * @param locale - The locale of the user
 * @param promptText - The prompt command text to handoff
 * @param destinationApp - The destination product or service to handoff the prompt to
 * @param fileId - The file id of the file to handoff to
 * @param abortController - The AbortController to cancel the handoff request
 * @returns handoffUrl to direct customer to in browser, or error if handoff failed
 */
export const handoffToAugloop = async (
  host: string,
  locale: string,
  prompt: Prompt,
  product: ProductType | string,
  file?: HandoffFile,
  abortController?: AbortController,
  msalContext?: IPublicClientApplication,
  userEmail?: string,
  isAgentPromptHandoffToAugloopEnabled?: boolean
): Promise<HandoffCallbackResult> => {
  const documentUrl = file?.fileUrl;
  const isWebUrl = file?.isWebUrl;
  const promptType = getPromptType(prompt.DisplayText);
  const agentContext = getAgentContext(
    prompt,
    isAgentPromptHandoffToAugloopEnabled
  );
  const context = JSON.stringify(
    agentContext == null ? { promptType } : { promptType, ...agentContext }
  );
  const queryId = generateGuid();

  const response: HandoffResponse = await handoffAsync(
    host,
    locale,
    product,
    prompt.CommandText,
    queryId,
    documentUrl ? "open" : "new",
    documentUrl,
    isWebUrl,
    context,
    msalContext
  );

  let handoffUrl =
    response && isError(response)
      ? ""
      : (response?.generatedLinks?.[0]?.platformHandoffUrl ?? "");

  if (userEmail) {
    const url = new URL(handoffUrl);
    url.searchParams.set("login_hint", userEmail);
    handoffUrl = url.toString();
  }

  if (!abortController?.signal.aborted) {
    if (isError(response)) {
      throw new Error("AugloopError");
    } else if (!handoffUrl) {
      throw new Error("NoResponse");
    }
  }

  return {
    handoffUrl,
    queryId,
  };
};
